<template>
    <div class="ns-vue-loader">
        <slot v-if="displayLoading" ref="loader" name="loader">
            <loader :dark="dark"></loader>
        </slot>
        <slot
            v-else-if="displayContent"
            ref="content"
            name="content"
        >
            <h1>{{ getVerbose('ns_loader_content') }}</h1>
        </slot>
        <slot v-if="dataError" name="error" >
            <div class="fetch-error">
                <i class="ti ti-alert"></i>
            </div>
        </slot>
    </div>
</template>

<script>
import { TimeTracking } from './time-tracking.js';
import {mapGetters} from 'vuex';
import Loader from '../partials/fundation-loader.vue';
export default {

    components: {
        Loader,
    },

    props: {
        loadingPromise: {
            type: Object,
            required: false,
            default: null,
        },
        toLoad: {
            type: [Object, Array],
            required: false,
            default(){
                return [];
            },
        },
        scoped: {
            type: Boolean,
            default: true,
        },
        customScope: {
            type: Object,
            required: false,
            default: null,
        },
        fetchTimer: {
            type: [Number, Object],
            default: 30000,
        },
        fetchImmediate: {
            type: Boolean,
            default: true,
        },
        showLoader: {
            type: Boolean,
            default: true,
        },
        onToLoadChangeOptions: {
            type: Object,
            required: false,
            default (){
                return {triggerRefresh: false, showLoading: false};
            }
        },
        // used for debug to show time elapsed for each fetch
        useTimeTracking: {
            type: Boolean,
            default: false,
        },
        allowOffline: {
            type: Boolean,
            default: false,
        },
        dark: {
            type: Boolean,
            default: false,
        },
    },

    data: function(){
        return {
            dataFetched: false,
            dataError: false,
            fetchTimeout: null,
            timeTracking: null,
            firstFetchDone: false,
        };
    },

    computed: {
        ...mapGetters('fundation', [
            'getVerbose'
        ]),
        displayLoading (){
            return !this.dataFetched && !this.dataError;
        },
        displayContent (){
            return !this.dataError && this.dataFetched;
        },
        canDisplayError (){
            return (!this.allowOffline || !this.firstFetchDone);
        },
    },

    watch: {
        toLoad: {
            handler (){
                if(this.onToLoadChangeOptions.triggerRefresh){
                    this.triggerRefresh(this.onToLoadChangeOptions.showLoading);
                }
            },
            deep: true,
        }
    },

    created: function(){
        if(this.fetchImmediate) this.init();
        if(!this.showLoader) this.dataFetched = true;
    },

    beforeDestroy (){
        clearTimeout(this.fetchTimeout);
    },

    methods: {
        init (){
            if(this.useTimeTracking){
                this.timeTracking = new TimeTracking();
            }
            let promises = [];
            let toLoad = this.toLoad;
            if(!(this.toLoad instanceof Array)){
                toLoad = [toLoad];
            }
            toLoad = this.toLoad.sort((a,b) => {
                let _a = a.hasOwnProperty('child');
                let _b = b.hasOwnProperty('child');
                return _a - _b;
            });
            if(this.loadingPromise){
                promises.push(this.loadingPromise);
            }else{
                promises = this.fetchOrGetData(toLoad);
            }
            let promise = Promise.all(promises).then(response => {
                this.firstFetchDone = true;
                let value = {};
                for( let item of response){
                    value = Object.assign(value, item);
                }
                this.data = value;
                this.dataFetched = true;
                this.dataError = false;
                this.$emit('dataFetched', value);
                if(this.useTimeTracking){
                    console.debug(this.timeTracking.getAllTime());
                    console.debug(this.timeTracking.getTimeByModel());
                }

            }).catch((e) => {
                if(this.canDisplayError){
                    console.error(e);
                    this.dataError = true;
                    this.dataFetched = false;
                }
                this.$emit('dataFetchError', e);
            });

            if(this.fetchTimer !== null){
                this.fetchTimeout = setTimeout(this.init, this.fetchTimer);
            }
            return promise;

        },

        fetchOrGetData (toLoad){
            let promises = [];

            let get = async (data, parentUid) =>{
                // special case for alarm / alert models, which are apparently loaded a cycle after the others (when not specified in project's meta, thus pushed in it by default if we allow alarms / alerts on the app)
                if (this.$store.state.api.meta.listModels[data.model] === undefined && (data.model === 'alarms' || data.model === 'alerts')) {
                    return;
                }
                let isSingleItem = data.uid !== undefined;
                let url = data.url ?
                    data.url:
                    this.$store.state.api.meta.listModels[data.model].url;
                if(isSingleItem){
                    url += `${data.uid}/`;
                }
                if(data.stats){
                    url += 'stats/';
                }
                else if(data.search && parentUid){
                    url += `?${data.search}=${parentUid}`;
                }
                url = this.addFilters(data.filters, url);
                let response;
                let value = {};
                let scopes;
                if(this.customScope){
                    scopes = this.customScope;
                }else{
                    scopes = (this.scoped)? this.$store.state.api.scopes : {};
                }
                const config = {headers: scopes};

                if(isSingleItem){
                    // fetch signle item
                    response = await this.$store.axios.get(url, config);
                    value[data.name] = response.data;
                }else{
                    // fetch collection
                    if(data.stats){
                        response = await this.$store.axios.get(url, config);
                        value[data.name] = response.data.objects_count;
                    }else if(data.noPaginate){
                        response = await this.$store.axios.get(url, config);
                        value[data.name] = response.data.results;
                        value[data.name + 'PaginateInfo'] = response;
                    }else{
                        try{
                            if(this.useTimeTracking){
                                this.timeTracking.addTracker(data.name, url);
                            }
                            response = await this.$store.dispatch('unpaginateResponse', {url, scopes});
                            if(this.useTimeTracking){
                                this.timeTracking.trackingEnded(url);
                            }
                        }
                        catch(error){
                            return Promise.reject(error);
                        }
                        value[data.name] = response.results;
                    }
                }

                if(data.hasOwnProperty('childs')){
                    for (let childData of data.childs){
                        let subValue = {};
                        if(response.hasOwnProperty('data')){
                            // if has own property 'data' then its from a child single item
                            subValue = await get(childData, response.data.uid);
                        }else if (response.hasOwnProperty('results')){
                            // else it is from a child with multiple items
                            for(let item of response.results){
                                let childRes = await get(childData, item.uid);
                                if(subValue.hasOwnProperty(childData.name)){
                                    subValue[childData.name].push(...childRes[childData.name]);
                                }else{
                                    subValue = childRes;
                                }
                            }
                        }
                        value = Object.assign(subValue, value);
                    }
                }
                return Promise.resolve(value);
            };

            for (let data of toLoad){
                promises.push(get(data));
            }
            return promises;
        },

        addFilters (filters={}, url){
            filters.c_resp_nested = filters.c_resp_nested || false;
            for(let key in filters){
                if(url.includes('?')) url += `&${key}=${filters[key]}`;
                else url += `?${key}=${filters[key]}`;
            }
            return url;
        },
        triggerRefresh (displayReload){
            this.dataFetched = !displayReload;
            clearTimeout(this.fetchTimeout);
            return this.init();
        },
    },
};
</script>

<style lang="scss">
.ns-vue-loader{
    position: relative;
    .el-card{
        position: relative;
        min-height: 400px;
        &.dark-el-card{
            background-color: rgba(0,0,0,0.4);
            border-color: #000;
        }
        .el-card__body{
            height: 100%;
            min-height: 360px; // 40px à cause du padding de el-card__body
        }
    }
    .fetch-error{
        position: absolute;
        top: calc(50% - 4px);
        left: 50%;
        transform: translate(-50%, -50%);
        color: #fff;
        font-size: 28px;
        z-index: 10000;
        .ti{
            cursor: pointer;
            &::after{
                overflow: hidden;
                white-space: nowrap;
                height: 30px;
                content: "Error while fetching. Retrying";
                position: absolute;
                bottom: -45px;
                width: 0px;
                text-align: center;
                left: 50%;
                transform: translate(-50%);
                background-color: #afafaf;
                font-size: 15px;
                padding: 5px 0;
                transition: width .5s;

            }
            &:hover{
                color: #E6A23C;
                &::after{
                    padding: 5px;
                    width: 210px;
                    color: #fff;
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// node_modules/fundation-app-vuejs/src/components/partials/ns-vue-loader.vue